import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store/hooks';
import { checkIfUserKnowsPassword } from '../store/slices/vaultFolderSlice';
import OpenTableModal from '../components/OpenTableModal';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store/store';
import { getPasswordsByVault, getPassword, getPasswordContent, createPassword, updatePassword, deletePassword } from '../store/slices/passwordSlice';
import { createTemplate, getTemplates, getTemplate, updateTemplate, deleteTemplate } from '../store/slices/templateSlice';
import type { TemplateItem } from '../store/slices/templateSlice';
import type { PasswordItem } from '../store/slices/passwordSlice';
import AddPasswordModal from '../components/AddPasswordModal';
import CreateTemplateModal, { type CreateTemplateModalResult } from '../components/CreateTemplateModal';
import EditPasswordForm, { type EditPasswordFormData } from '../components/EditPasswordForm';
import ConfirmationModal from '../components/Helper/ConfirmationModal';
import type { SelectedTableInfo } from './Workspace';

interface PasswordData {
  id: string;
  title: string;
  createdBy: string;
  updatedBy: string;
  username: string;
  password: string;
  website: string;
  category: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

interface SheetTab {
  id: string;
  name: string;
  data: PasswordData[];
}

/** UTC olarak tutulan ISO date string'i kullanıcının yerel saatine çevirip formatlar (örn: 18.02.2026 11:54). */
function formatDateTime(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const trimmed = isoString.trim();
  const asUtc = /[Z+-]\d{2}:?\d{2}$/.test(trimmed) ? trimmed : `${trimmed.replace(/Z$/i, '')}Z`;
  const date = new Date(asUtc);
  if (Number.isNaN(date.getTime())) return isoString;
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

interface PasswordsLocationState {
  selectedTables: SelectedTableInfo[];
  /** Tek vault için (geriye dönük uyumluluk) */
  vaultPassword?: string;
  /** Her vault için ayrı şifre: tableId -> vaultMasterPassword */
  vaultPasswords?: Record<string, string>;
}

function normalizeSelectedTables(state: unknown): SelectedTableInfo[] | null {
  if (Array.isArray(state) && state.length > 0) {
    return state.every(
      (s) => s && typeof s.tableId === 'string' && typeof s.tableName === 'string'
    )
      ? (state as SelectedTableInfo[])
      : null;
  }
  if (
    state &&
    typeof state === 'object' &&
    'selectedTables' in state &&
    Array.isArray((state as PasswordsLocationState).selectedTables)
  ) {
    const arr = (state as PasswordsLocationState).selectedTables;
    return arr.length > 0 &&
      arr.every(
        (s) => s && typeof s.tableId === 'string' && typeof s.tableName === 'string'
      )
      ? arr
      : null;
  }
  if (state && typeof state === 'object' && 'tableId' in state && 'tableName' in state) {
    return [state as SelectedTableInfo];
  }
  return null;
}

function getVaultPasswordFromState(state: unknown): string | undefined {
  if (state && typeof state === 'object' && 'vaultPassword' in state) {
    const v = (state as PasswordsLocationState).vaultPassword;
    return typeof v === 'string' ? v : undefined;
  }
  return undefined;
}

/** Belirli bir vault için şifre (vaultPasswords varsa ondan, yoksa vaultPassword) */
function getVaultPasswordForVault(state: unknown, vaultId: string): string | undefined {
  if (state && typeof state === 'object') {
    const s = state as PasswordsLocationState;
    if (s.vaultPasswords && typeof s.vaultPasswords[vaultId] === 'string') {
      return s.vaultPasswords[vaultId];
    }
    if (typeof s.vaultPassword === 'string') return s.vaultPassword;
  }
  return undefined;
}

function mapPasswordItemToGridData(item: PasswordItem): PasswordData {
  return {
    id: item.id,
    title: item.title ?? '',
    createdBy: item.createdBy ?? '',
    updatedBy: item.updatedBy ?? '',
    username: '',
    password: '',
    website: '',
    category: item.category ?? '',
    notes: '',
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

const Passwords: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedTables = normalizeSelectedTables(location.state);
  const vaultPassword = getVaultPasswordFromState(location.state);

  const [searchText, setSearchText] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [, setSelectedCredential] = useState<PasswordData | null>(null);
  const [editCredentialFormData, setEditCredentialFormData] = useState<EditPasswordFormData | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [credentialsToDelete, setCredentialsToDelete] = useState<string[]>([]);
  const [isOpenTableModalOpen, setIsOpenTableModalOpen] = useState(false);
  const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] = useState(false);
  const [isMyTemplatesModalOpen, setIsMyTemplatesModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isEditTemplateModalOpen, setIsEditTemplateModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [editingTemplateName, setEditingTemplateName] = useState('');
  const [editingTemplateContent, setEditingTemplateContent] = useState('');
  const [showDeleteTemplateConfirm, setShowDeleteTemplateConfirm] = useState(false);
  const [templateToDeleteId, setTemplateToDeleteId] = useState<string | null>(null);
  const [pendingEditTemplateId, setPendingEditTemplateId] = useState<string | null>(null);
  const templateItems = useSelector((state: RootState) => state.template.items);
  const templateListLoading = useSelector((state: RootState) => state.template.operationLoading.list);
  const templateListError = useSelector((state: RootState) => state.template.errors.list);
  const templateGetLoading = useSelector((state: RootState) => state.template.operationLoading.get);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');

  const initialSheetsFromState = useMemo<SheetTab[]>(() => {
    if (!selectedTables?.length) return [];
    return selectedTables.map((s) => ({
      id: s.tableId,
      name: s.tableName,
      data: [],
    }));
  }, [selectedTables]);

  useEffect(() => {
    if (!selectedTables?.length) {
      navigate('/workspace', { replace: true });
    }
  }, [selectedTables, navigate]);

  const [sheets, setSheets] = useState<SheetTab[]>(() =>
    initialSheetsFromState.length > 0
      ? initialSheetsFromState
      : [{ id: `sheet-${Date.now()}-1`, name: t('pages.passwords.title'), data: [] }]
  );
  const [activeSheetId, setActiveSheetId] = useState<string | null>(() =>
    initialSheetsFromState[0]?.id ?? null
  );

  const activeSheet = useMemo(
    () => sheets.find((s) => s.id === (activeSheetId ?? sheets[0]?.id)) ?? sheets[0],
    [sheets, activeSheetId]
  );
  const rowData = useMemo(() => activeSheet?.data ?? [], [activeSheet?.data]);

  /** Arama metnine göre filtrelenmiş satırlar (s-grid tablo için). */
  const filteredRowData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rowData;
    return rowData.filter(
      (row) =>
        (row.title ?? '').toLowerCase().includes(q) ||
        (row.category ?? '').toLowerCase().includes(q) ||
        (row.createdBy ?? '').toLowerCase().includes(q) ||
        (row.updatedBy ?? '').toLowerCase().includes(q) ||
        formatDateTime(row.createdAt).toLowerCase().includes(q) ||
        formatDateTime(row.updatedAt).toLowerCase().includes(q)
    );
  }, [rowData, searchText]);

  /** Kanban sütunları: filteredRowData üzerinden benzersiz kategoriler (boş = "Kategorisiz" sütunu, en başta). */
  const kanbanColumns = useMemo(() => {
    const cats = new Set<string>();
    filteredRowData.forEach((row) => cats.add(row.category?.trim() ?? ''));
    return Array.from(cats).sort((a, b) =>
      (a === '' ? 0 : 1) - (b === '' ? 0 : 1) || a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [filteredRowData]);

  /** Kanban: her sütun (category key) için o kategorideki kartlar. */
  const kanbanCardsByColumn = useMemo(() => {
    const map = new Map<string, PasswordData[]>();
    kanbanColumns.forEach((col) => map.set(col, []));
    filteredRowData.forEach((row) => {
      const key = row.category?.trim() ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    });
    return map;
  }, [kanbanColumns, filteredRowData]);

  /** Tüm vault'lar için şifreleri API'den yükler (ekran açılışı, add/edit sonrası, refresh). */
  const loadAllVaultPasswords = useCallback(async () => {
    if (!selectedTables?.length) return;
    setIsTableLoading(true);
    const results: { vaultId: string; data: PasswordData[] }[] = [];
    try {
      for (const table of selectedTables) {
        const pwd = getVaultPasswordForVault(location.state, table.tableId) ?? vaultPassword;
        if (!pwd?.trim()) continue;
        try {
          const { items } = await dispatch(
            getPasswordsByVault({ vaultId: table.tableId, vaultMasterPassword: pwd })
          ).unwrap();
          results.push({
            vaultId: table.tableId,
            data: (items ?? []).map(mapPasswordItemToGridData),
          });
        } catch {
          results.push({ vaultId: table.tableId, data: [] });
        }
      }
      setSheets((prev) =>
        prev.map((sheet) => {
          const found = results.find((r) => r.vaultId === sheet.id);
          return found ? { ...sheet, data: found.data } : sheet;
        })
      );
    } finally {
      setIsTableLoading(false);
    }
  }, [selectedTables, dispatch, location.state, vaultPassword]);

  useEffect(() => {
    loadAllVaultPasswords();
  }, [loadAllVaultPasswords]);

  useEffect(() => {
    if (isMyTemplatesModalOpen) {
      dispatch(getTemplates());
      setSelectedTemplateId(null);
      setIsEditTemplateModalOpen(false);
      setShowDeleteTemplateConfirm(false);
      setTemplateToDeleteId(null);
      setPendingEditTemplateId(null);
    }
  }, [isMyTemplatesModalOpen, dispatch]);

  const selectedTemplate = useMemo(
    () => (selectedTemplateId ? templateItems.find((i) => i.id === selectedTemplateId) ?? null : null),
    [selectedTemplateId, templateItems]
  );

  useEffect(() => {
    if (
      pendingEditTemplateId &&
      selectedTemplateId === pendingEditTemplateId &&
      selectedTemplate &&
      (selectedTemplate.content != null || !templateGetLoading)
    ) {
      setEditingTemplateId(selectedTemplate.id);
      setEditingTemplateName(selectedTemplate.name ?? '');
      setEditingTemplateContent(selectedTemplate.content ?? '');
      setIsEditTemplateModalOpen(true);
      setPendingEditTemplateId(null);
    }
  }, [pendingEditTemplateId, selectedTemplateId, selectedTemplate, templateGetLoading]);

  /** Şablona tıklanınca doğrudan düzenleme modalını açar; içerik yoksa önce yükler. */
  const handleTemplateItemClick = useCallback(
    (item: TemplateItem) => {
      setSelectedTemplateId(item.id);
      if (item.content == null || item.content === '') {
        dispatch(getTemplate(item.id));
      }
      setPendingEditTemplateId(item.id);
    },
    [dispatch]
  );

  const handleStartEditTemplate = useCallback(() => {
    if (selectedTemplate) {
      setEditingTemplateId(selectedTemplate.id);
      setEditingTemplateName(selectedTemplate.name ?? '');
      setEditingTemplateContent(selectedTemplate.content ?? '');
      setIsEditTemplateModalOpen(true);
    }
  }, [selectedTemplate]);

  const handleConfirmDeleteTemplate = useCallback(async () => {
    if (!templateToDeleteId) return;
    try {
      await dispatch(deleteTemplate(templateToDeleteId)).unwrap();
      setShowDeleteTemplateConfirm(false);
      setTemplateToDeleteId(null);
      setSelectedTemplateId(null);
      await dispatch(getTemplates());
    } catch {
      setShowDeleteTemplateConfirm(false);
      setTemplateToDeleteId(null);
    }
  }, [templateToDeleteId, dispatch]);

  /** Yeni tablo (vault) açma modal'ından seçilen tabloyu ekler; şifre doğrulanır, state güncellenir. */
  const handleOpenTableFromModal = useCallback(
    async (info: SelectedTableInfo, vaultMasterPassword: string) => {
      const result = await dispatch(
        checkIfUserKnowsPassword({ vaultId: info.tableId, vaultMasterPassword })
      ).unwrap();
      if (result !== true) {
        throw new Error('Invalid vault password');
      }
      const newSelectedTables = [...(selectedTables ?? []), info];
      const currentVaultPasswords = (location.state as PasswordsLocationState)?.vaultPasswords ?? {};
      const newVaultPasswords = { ...currentVaultPasswords, [info.tableId]: vaultMasterPassword };
      setSheets((prev) => [...prev, { id: info.tableId, name: info.tableName, data: [] }]);
      setActiveSheetId(info.tableId);
      navigate('/passwords', {
        state: { selectedTables: newSelectedTables, vaultPasswords: newVaultPasswords },
        replace: true,
      });
      setIsOpenTableModalOpen(false);
    },
    [dispatch, selectedTables, location.state, navigate]
  );

  const handleAddPassword = async (data: { id: string; title: string; content: string; category?: string | null }) => {
    if (!activeSheet) return;
    const vaultId = activeSheet.id;
    const title = data.title?.trim() ?? '';
    const content = data.content?.trim() ?? '';
    const category = data.category?.trim() || null;
    try {
      await dispatch(
        createPassword({ vaultId, title: title || null, content: content || null, category })
      ).unwrap();
      setIsAddModalOpen(false);
      await loadAllVaultPasswords();
    } catch {
      // Hata durumunda modal açık kalır
    }
  };

  const handleEditPassword = async (data: { id: string; title: string; content: string; category?: string | null }) => {
    try {
      await dispatch(
        updatePassword({
          id: data.id,
          body: { title: data.title, content: data.content, category: data.category?.trim() || null },
        })
      ).unwrap();
      setIsEditModalOpen(false);
      setSelectedCredential(null);
      setEditCredentialFormData(null);
      await loadAllVaultPasswords();
    } catch {
      // Hata durumunda modal açık kalır; kullanıcı tekrar deneyebilir
    }
  };

  const openEditModalWithPassword = useCallback(
    async (rowData: PasswordData) => {
      setSelectedCredential(null);
      setEditCredentialFormData(null);
      try {
        const [item, contentResult] = await Promise.all([
          dispatch(getPassword(rowData.id)).unwrap(),
          dispatch(getPasswordContent(rowData.id)).unwrap().catch(() => ({ content: '' })),
        ]);
        setSelectedCredential(mapPasswordItemToGridData(item));
        setEditCredentialFormData({
          id: item.id,
          title: item.title ?? '',
          notes: contentResult?.content ?? '',
          category: item.category ?? '',
        });
        setIsEditModalOpen(true);
      } catch {
        setSelectedCredential(rowData);
        setEditCredentialFormData({
          id: rowData.id,
          title: rowData.title,
          notes: rowData.notes ?? '',
          category: rowData.category ?? '',
        });
        setIsEditModalOpen(true);
      }
    },
    [dispatch]
  );

  const toggleRowSelection = useCallback((id: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    const multi = event ? event.ctrlKey || event.metaKey : false;
    if (multi) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      return true;
    }
    return false;
  }, []);

  const handleDeleteClick = () => {
    if (selectedIds.size > 0) {
      setCredentialsToDelete(Array.from(selectedIds));
      setShowDeleteConfirmModal(true);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await Promise.all(credentialsToDelete.map((id) => dispatch(deletePassword(id)).unwrap()));
      setShowDeleteConfirmModal(false);
      setCredentialsToDelete([]);
      await loadAllVaultPasswords();
    } catch {
      setShowDeleteConfirmModal(false);
      setCredentialsToDelete([]);
      await loadAllVaultPasswords();
    }
  };

  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const handleDuplicateClick = async () => {
    if (!activeSheet || selectedIds.size === 0) return;
    const selectedRows = rowData.filter((r) => selectedIds.has(r.id));
    if (selectedRows.length === 0) return;
    const vaultId = activeSheet.id;
    const suffix = t('pages.passwords.duplicateTitleSuffix', ' (Kopya)');
    setIsDuplicateLoading(true);
    try {
      for (const row of selectedRows) {
        const [item, contentResult] = await Promise.all([
          dispatch(getPassword(row.id)).unwrap(),
          dispatch(getPasswordContent(row.id)).unwrap().catch(() => ({ content: '' })),
        ]);
        const title = (item.title ?? '').trim() || t('pages.passwords.duplicateDefaultTitle', 'Kopya');
        await dispatch(
          createPassword({
            vaultId,
            title: title + suffix,
            content: contentResult?.content ?? null,
            category: item.category ?? null,
          })
        ).unwrap();
      }
      await loadAllVaultPasswords();
    } finally {
      setIsDuplicateLoading(false);
    }
  };

  const selectedRowCount = selectedIds.size;

  const addFormInitialData: EditPasswordFormData = { id: '', title: '', notes: '', category: '' };

  /** Aktif vault'taki kayıtlardan benzersiz kategorileri (alfabetik) toplar; ekleme/düzenleme formunda öneri olarak kullanılır. */
  const existingCategories = useMemo(() => {
    const cats = new Set<string>();
    (activeSheet?.data ?? []).forEach((row) => {
      const c = row.category?.trim();
      if (c) cats.add(c);
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [activeSheet?.data]);

  if (!selectedTables?.length) {
    return null;
  }

  return (
    <div className="space-y-4 2xl:space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/80 dark:border-gray-700 shadow-md shadow-gray-200/50 dark:shadow-none dark:border-gray-700/80 overflow-hidden">
        <div className="px-3 lg:px-5 py-3 2xl:py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800/95">
          <s-grid gridTemplateColumns="1fr auto" gap="base" style={{ alignItems: 'center' }}>
            <div className="flex items-center flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                title={t('pages.passwords.addPassword')}
                className="px-4 py-2.5 bg-primary text-white rounded-xl font-medium shadow-sm hover:shadow hover:bg-blue-600 transition-all duration-200 flex items-center gap-2"
              >
                <i className="fa-solid fa-plus"></i>
                {t('pages.passwords.addPassword')}
              </button>
              <span className="w-px h-6 bg-gray-300 dark:bg-gray-600 self-center" aria-hidden />
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={selectedRowCount === 0}
                title={t('pages.passwords.delete')}
                className="px-3.5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fa-solid fa-trash"></i>
                {t('pages.passwords.delete')}
              </button>
              <button
                type="button"
                onClick={handleDuplicateClick}
                disabled={selectedRowCount === 0 || isDuplicateLoading}
                title={t('pages.passwords.duplicate')}
                className="px-3.5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDuplicateLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {t('common.loading', 'Yükleniyor...')}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-copy"></i>
                    {t('pages.passwords.duplicate')}
                  </>
                )}
              </button>
              <span className="w-px h-6 bg-gray-300 dark:bg-gray-600 self-center" aria-hidden />
              <button
                type="button"
                onClick={() => setIsMyTemplatesModalOpen(true)}
                title={t('pages.passwords.myTemplates')}
                className="px-3.5 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/80 transition-colors flex items-center gap-2"
              >
                <i className="fa-solid fa-folder-open"></i>
                {t('pages.passwords.myTemplates')}
              </button>
            </div>
            <button
              type="button"
              onClick={() => loadAllVaultPasswords()}
              disabled={isTableLoading}
              className="p-2.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0 disabled:opacity-50 min-w-[44px]"
              title={t('pages.passwords.refresh')}
            >
              <i className={isTableLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-arrows-rotate'}></i>
            </button>
          </s-grid>
        </div>

        <s-grid gridTemplateRows="auto 1fr" gap="none" style={{ minHeight: 0 }}>
        {/* Vault sekmeleri */}
        <div className="flex items-center gap-0 border-b border-gray-200 dark:border-gray-700 bg-gray-100/80 dark:bg-gray-800/60 overflow-x-auto min-h-[42px]">
          {sheets.map((sheet) => {
            const isActive = sheet.id === activeSheet?.id;
            return (
              <div
                key={sheet.id}
                role="tab"
                aria-selected={isActive}
                className={`
                  group flex items-center gap-1.5 pr-2 py-2.5 pl-4 text-sm font-medium whitespace-nowrap border-r border-gray-200 dark:border-gray-600
                  transition-all duration-200 shrink-0 cursor-pointer
                  ${isActive
                    ? 'bg-white dark:bg-gray-800 text-primary border-b-2 border-b-primary -mb-px shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200/80 dark:hover:bg-gray-700/80'
                  }
                `}
                onClick={() => setActiveSheetId(sheet.id)}
              >
                <span className="flex-1 min-w-0 truncate">{sheet.name}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const index = sheets.findIndex((s) => s.id === sheet.id);
                    const nextSheets = sheets.filter((s) => s.id !== sheet.id);
                    if (nextSheets.length === 0) {
                      navigate('/workspace', { replace: true });
                      return;
                    }
                    setSheets(nextSheets);
                    if (sheet.id === activeSheetId) {
                      const newIndex = Math.min(index, nextSheets.length - 1);
                      setActiveSheetId(nextSheets[newIndex >= 0 ? newIndex : 0].id);
                    }
                    // Kalan sekmelere göre location.state güncelle; sayfa yenilense bile doğru vault listesi kalsın
                    const remainingIds = new Set(nextSheets.map((s) => s.id));
                    const newSelectedTables = (selectedTables ?? []).filter((t) => remainingIds.has(t.tableId));
                    const currentVaultPasswords = (location.state as PasswordsLocationState)?.vaultPasswords ?? {};
                    const newVaultPasswords: Record<string, string> = {};
                    newSelectedTables.forEach((t) => {
                      if (typeof currentVaultPasswords[t.tableId] === 'string') {
                        newVaultPasswords[t.tableId] = currentVaultPasswords[t.tableId];
                      }
                    });
                    navigate('/passwords', {
                      state: { selectedTables: newSelectedTables, vaultPasswords: newVaultPasswords },
                      replace: true,
                    });
                  }}
                  className="p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 text-current opacity-70 hover:opacity-100 transition-opacity"
                  aria-label={t('pages.passwords.closeTab')}
                >
                  <i className="fa-solid fa-times text-xs"></i>
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => setIsOpenTableModalOpen(true)}
            className="px-3 py-2.5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300 shrink-0 transition-colors flex items-center gap-2 rounded-br-lg"
            title={t('pages.passwords.openTableButton', 'Vault aç')}
          >
            <i className="fa-solid fa-plus text-xs"></i>
            <span className="text-sm font-medium">{t('pages.passwords.openTableButton', 'Vault aç')}</span>
          </button>
        </div>

        <div className="px-3 lg:px-5 py-3 2xl:py-4 min-h-[300px] md:min-h-[450px] 2xl:min-h-[600px] max-h-[1200px] overflow-auto passwords-table-wrapper" data-passwords-table>
          {/* Arama + Görünüm seçici */}
          <div className="flex items-center gap-3 flex-wrap p-4 mb-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
            <div className="relative flex-1 min-w-0 sm:min-w-[220px]">
              <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm pointer-events-none"></i>
              <input
                type="text"
                placeholder={t('pages.passwords.searchPlaceholder')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none text-sm shadow-sm hover:shadow"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  aria-label={t('pages.passwords.clearSearch')}
                >
                  <i className="fa-solid fa-times text-xs"></i>
                </button>
              )}
            </div>
            <div className="flex items-center rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/80 overflow-hidden shadow-sm" role="group" aria-label={t('pages.passwords.view.label')}>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-3.5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80'}`}
                aria-pressed={viewMode === 'table'}
              >
                <i className="fa-solid fa-table-list"></i>
                {t('pages.passwords.view.table')}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('kanban')}
                className={`px-3.5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 border-l border-gray-200 dark:border-gray-600 ${viewMode === 'kanban' ? 'bg-primary text-white border-l-transparent' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/80'}`}
                aria-pressed={viewMode === 'kanban'}
              >
                <i className="fa-solid fa-columns"></i>
                {t('pages.passwords.view.kanban')}
              </button>
            </div>
          </div>

          {/* Loading */}
          {isTableLoading && (
            <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
              <i className="fa-solid fa-spinner fa-spin text-2xl mr-2"></i>
              <span>{t('common.loading', 'Yükleniyor...')}</span>
            </div>
          )}

          {/* Tablo – div tabanlı, efektler kesin görünür */}
          {!isTableLoading && viewMode === 'table' && (
            <div className="passwords-table rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 shadow-sm">
              {/* Header */}
              <div className="passwords-table-header hidden md:grid grid-cols-[1fr_0.8fr_1fr_1fr] gap-0 min-h-[48px] bg-gradient-to-b from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-200 dark:border-gray-600">
                <div className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                  {t('pages.passwords.columns.title')}
                </div>
                <div className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                  {t('pages.passwords.columns.category')}
                </div>
                <div className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                  {t('pages.passwords.columns.createdBy', 'Oluşturan')} / {t('pages.passwords.columns.created')}
                </div>
                <div className="px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                  {t('pages.passwords.columns.updatedBy', 'Güncelleyen')} / {t('pages.passwords.columns.updated')}
                </div>
              </div>
              {/* Body */}
              <div className="passwords-table-body">
                {filteredRowData.map((row, index) => {
                  const isSelected = selectedIds.has(row.id);
                  const isEven = index % 2 === 1;
                  return (
                    <div
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        if (toggleRowSelection(row.id, e)) return;
                        setSelectedIds(new Set([row.id]));
                        openEditModalWithPassword(row);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (toggleRowSelection(row.id, e)) return;
                          setSelectedIds(new Set([row.id]));
                          openEditModalWithPassword(row);
                        }
                      }}
                      aria-pressed={isSelected}
                      className={`
                        passwords-table-row flex flex-col gap-1 md:grid md:grid-cols-[1fr_0.8fr_1fr_1fr] md:gap-0 p-3 md:p-0 min-h-[52px] cursor-pointer
                        border-b border-gray-100 dark:border-gray-700/80
                        transition-[background-color,border-color,box-shadow] duration-200 ease-out
                        border-l-4 border-l-transparent
                        ${isEven ? 'bg-gray-50/60 dark:bg-gray-800/40' : 'bg-white dark:bg-gray-800/20'}
                        hover:bg-blue-50/90 hover:border-l-blue-500 dark:hover:bg-blue-900/30 dark:hover:border-l-blue-400
                        ${isSelected ? '!bg-blue-100/90 !border-l-blue-600 dark:!bg-blue-900/50 dark:!border-l-blue-500 shadow-[inset_0_0_0_2px_rgba(59,130,246,0.25)]' : ''}
                      `}
                    >
                      <div className="flex items-center gap-2 px-0 py-1 md:px-4 md:py-3">
                        <span className="md:hidden text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{t('pages.passwords.columns.title')}</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">{row.title || '—'}</span>
                      </div>
                      <div className="flex items-center gap-2 px-0 py-1 md:px-4 md:py-3">
                        <span className="md:hidden text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{t('pages.passwords.columns.category')}</span>
                        {row.category ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200/80 dark:border-gray-600/80">
                            {row.category}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </div>
                      <div className="flex flex-col justify-center px-0 py-1 md:px-4 md:py-3">
                        <span className="md:hidden text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{t('pages.passwords.columns.createdBy', 'Oluşturan')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{row.createdBy || '—'}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{formatDateTime(row.createdAt)}</span>
                      </div>
                      <div className="flex flex-col justify-center px-0 py-1 md:px-4 md:py-3">
                        <span className="md:hidden text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{t('pages.passwords.columns.updatedBy', 'Güncelleyen')}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{row.updatedBy || '—'}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{formatDateTime(row.updatedAt)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Kanban – kategori sütunları, kartlar */}
          {!isTableLoading && viewMode === 'kanban' && filteredRowData.length > 0 && (
            <div className="passwords-kanban flex gap-3 2xl:gap-4 overflow-x-auto pb-2 min-h-[300px] 2xl:min-h-[400px]" data-passwords-kanban>
              {kanbanColumns.map((categoryKey) => {
                const columnTitle = categoryKey === '' ? t('pages.passwords.view.uncategorized') : categoryKey;
                const cards = kanbanCardsByColumn.get(categoryKey) ?? [];
                return (
                  <div
                    key={categoryKey || '__uncategorized__'}
                    className="passwords-kanban-column shrink-0 w-[280px] flex flex-col rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/50 shadow-sm overflow-hidden"
                  >
                    <div className="px-3 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100/80 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                      <span className="truncate block">{columnTitle}</span>
                      <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({cards.length})</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[120px]">
                      {cards.map((row) => {
                        const isSelected = selectedIds.has(row.id);
                        return (
                          <div
                            key={row.id}
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              if (toggleRowSelection(row.id, e)) return;
                              setSelectedIds(new Set([row.id]));
                              openEditModalWithPassword(row);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (toggleRowSelection(row.id, e)) return;
                                setSelectedIds(new Set([row.id]));
                                openEditModalWithPassword(row);
                              }
                            }}
                            aria-pressed={isSelected}
                            className={`passwords-kanban-card px-3 py-2.5 rounded-lg border cursor-pointer text-left transition-all duration-200
                              bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600
                              hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md
                              ${isSelected ? 'ring-2 ring-primary border-primary dark:border-primary bg-blue-50/80 dark:bg-blue-900/30' : ''}`}
                          >
                            <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">{row.title || '—'}</div>
                            {(row.createdBy || row.createdAt) && (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                                {row.createdBy && <span>{row.createdBy}</span>}
                                {row.createdAt && <span className="tabular-nums ml-1">{formatDateTime(row.createdAt)}</span>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!isTableLoading && filteredRowData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-6 2xl:py-24 2xl:px-8 text-center rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-600 bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-800/40 dark:to-gray-800/20 mt-3">
              <div className="rounded-2xl bg-white/90 dark:bg-gray-700/40 p-6 mb-6 shadow-sm ring-1 ring-gray-200/50 dark:ring-gray-600/30">
                <i className={`fa-solid text-5xl text-gray-300 dark:text-gray-500 ${searchText ? 'fa-search' : 'fa-key'}`}></i>
              </div>
              <p className="text-lg 2xl:text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2 tracking-tight">
                {searchText ? t('pages.passwords.noSearchResults', 'Eşleşen kayıt yok') : t('pages.passwords.emptyState', 'Henüz kayıt yok')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed">
                {searchText
                  ? t('pages.passwords.noSearchResultsHint', 'Arama metnini değiştirmeyi veya temizlemeyi deneyin.')
                  : t('pages.passwords.emptyStateHint', 'Yeni kayıt eklemek için yukarıdaki "Şifre Ekle" butonunu kullanın.')}
              </p>
            </div>
          )}
        </div>
        </s-grid>
      </div>

      <AddPasswordModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title={t('pages.passwords.modals.addCredential')}
        maxWidth="max-w-7xl"
      >
        <EditPasswordForm
          initialData={addFormInitialData}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddPassword}
          suggestedCategories={existingCategories}
          onCreateTemplate={() => setIsCreateTemplateModalOpen(true)}
          singleViewMode
        />
      </AddPasswordModal>

      <AddPasswordModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCredential(null);
          setEditCredentialFormData(null);
        }}
        title={t('pages.passwords.modals.editCredential')}
        maxWidth="max-w-7xl"
      >
        {editCredentialFormData && (
          <EditPasswordForm
            initialData={editCredentialFormData}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedCredential(null);
              setEditCredentialFormData(null);
            }}
            onSave={handleEditPassword}
            suggestedCategories={existingCategories}
            onCreateTemplate={() => setIsCreateTemplateModalOpen(true)}
            singleViewMode
            startInPreview
            onDelete={(id) => {
              setCredentialsToDelete([id]);
              setIsEditModalOpen(false);
              setEditCredentialFormData(null);
              setShowDeleteConfirmModal(true);
            }}
          />
        )}
      </AddPasswordModal>

      <ConfirmationModal
        isOpen={showDeleteConfirmModal}
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setCredentialsToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        title={t('pages.passwords.modals.deleteCredential')}
        message={
          credentialsToDelete.length === 1
            ? t('pages.passwords.modals.deleteSingleMessage')
            : t('pages.passwords.modals.deleteMultipleMessage', { count: credentialsToDelete.length })
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmButtonStyle="danger"
      />

      <ConfirmationModal
        isOpen={showDeleteTemplateConfirm}
        onClose={() => {
          setShowDeleteTemplateConfirm(false);
          setTemplateToDeleteId(null);
        }}
        onConfirm={handleConfirmDeleteTemplate}
        title={t('pages.passwords.templateModal.deleteTitle', 'Şablonu sil')}
        message={t('pages.passwords.templateModal.deleteMessage', 'Bu şablonu silmek istediğinize emin misiniz?')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        confirmButtonStyle="danger"
      />

      <OpenTableModal
        isOpen={isOpenTableModalOpen}
        onClose={() => setIsOpenTableModalOpen(false)}
        alreadyOpenTableIds={sheets.map((s) => s.id)}
        onOpenTable={handleOpenTableFromModal}
      />

      <AddPasswordModal
        isOpen={isMyTemplatesModalOpen}
        onClose={() => setIsMyTemplatesModalOpen(false)}
        title={t('pages.passwords.myTemplates')}
        maxWidth="max-w-2xl"
      >
        <div className="p-4 space-y-3">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsCreateTemplateModalOpen(true)}
              className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <i className="fa-solid fa-plus" />
              {t('pages.passwords.createTemplate')}
            </button>
          </div>
          {templateListLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
              <i className="fa-solid fa-spinner fa-spin text-xl mr-2" />
              <span className="text-sm">{t('common.loading', 'Yükleniyor...')}</span>
            </div>
          ) : templateListError ? (
            <div className="py-3 px-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              {templateListError}
            </div>
          ) : templateItems.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-6 text-sm">
              {t('pages.passwords.templateModal.noTemplates')}
            </p>
          ) : (
            <>
              <ul className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                {templateItems.map((item: TemplateItem) => (
                  <li
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleTemplateItemClick(item)}
                    onKeyDown={(e) => e.key === 'Enter' && handleTemplateItemClick(item)}
                    className={`flex items-center justify-between py-2 px-3 rounded-md border cursor-pointer transition-colors ${
                      selectedTemplateId === item.id
                        ? 'border-primary bg-primary/10 dark:bg-primary/20'
                        : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex-1 min-w-0">
                      {item.name ?? item.id}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 shrink-0">
                      {formatDateTime(item.updatedAt)}
                    </span>
                    <div className="flex items-center gap-0.5 shrink-0 ml-1.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedTemplateId === item.id) {
                            handleStartEditTemplate();
                          } else {
                            setPendingEditTemplateId(item.id);
                            handleTemplateItemClick(item);
                          }
                        }}
                        disabled={isEditTemplateModalOpen}
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-primary/10 rounded transition-colors disabled:opacity-50"
                        title={t('common.edit', 'Düzenle')}
                        aria-label={t('common.edit', 'Düzenle')}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTemplateToDeleteId(item.id);
                          setShowDeleteTemplateConfirm(true);
                        }}
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        title={t('common.delete', 'Sil')}
                        aria-label={t('common.delete', 'Sil')}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </AddPasswordModal>

      <CreateTemplateModal
        isOpen={isCreateTemplateModalOpen}
        onClose={() => setIsCreateTemplateModalOpen(false)}
        onSave={async (data: CreateTemplateModalResult) => {
          try {
            await dispatch(
              createTemplate({ name: data.title || null, content: data.content || null })
            ).unwrap();
            setIsCreateTemplateModalOpen(false);
            await dispatch(getTemplates());
          } catch {
            // Hata durumunda modal açık kalır
          }
        }}
        modalTitle={t('pages.passwords.templateModal.title')}
        titleLabel={t('pages.passwords.templateModal.titleLabel')}
        titlePlaceholder={t('pages.passwords.templateModal.titlePlaceholder')}
        contentLabel={t('pages.passwords.templateModal.fieldsLabel')}
        contentPlaceholder={t('pages.passwords.templateModal.fieldPlaceholder')}
        contentRequiredError={t('pages.passwords.templateModal.fieldRequired')}
        saveLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
      />

      <CreateTemplateModal
        isOpen={isEditTemplateModalOpen}
        onClose={() => setIsEditTemplateModalOpen(false)}
        templateId={editingTemplateId ?? ''}
        initialTitle={editingTemplateName}
        initialContent={editingTemplateContent}
        onUpdate={async (id, data) => {
          await dispatch(
            updateTemplate({
              id,
              body: { name: data.title || null, content: data.content || null },
            })
          ).unwrap();
          setIsEditTemplateModalOpen(false);
          await dispatch(getTemplate(id)).unwrap();
          await dispatch(getTemplates());
        }}
        modalTitle={t('pages.passwords.templateModal.editTitle')}
        titleLabel={t('pages.passwords.templateModal.titleLabel')}
        titlePlaceholder={t('pages.passwords.templateModal.titlePlaceholder')}
        contentLabel={t('pages.passwords.templateModal.fieldsLabel')}
        contentPlaceholder={t('pages.passwords.templateModal.fieldPlaceholder')}
        contentRequiredError={t('pages.passwords.templateModal.fieldRequired')}
        saveLabel={t('common.save')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
};

export default Passwords;
